export type ProofStepId = 'supply' | 'knockout' | 'drain' | 'highloop' | 'overview'

export interface DetectionBoxSpec {
  top: string
  left: string
  width: string
  height: string
  text: string
}

export interface ProofStep {
  id: ProofStepId
  num: string
  label: string
  basePhoto: string
  capture: string[]
  why: string
  primerBox: DetectionBoxSpec[]
  cameraBox: DetectionBoxSpec[]
  verify: {
    outcomeTitle: string
    outcomeBody: string
    passBox: DetectionBoxSpec[]
  }
}

export const PROOF_STEPS: ProofStep[] = [
  {
    id: 'supply',
    num: '1',
    label: 'Supply Valve Connection',
    basePhoto: '/proof/proof-1-supply.jpg',
    capture: [
      'Compression nut at shutoff valve',
      'Threads clearly visible',
      '6–8 inches from connection',
    ],
    why: 'Thread engagement is the primary evidence in under-tight vs over-tight claims. A clean close-up resolves this dispute at the source.',
    primerBox: [
      { top: '32.67%', left: '54%', width: '34%', height: '40.67%', text: 'THREAD_ENGAGE' },
    ],
    cameraBox: [
      { top: '36%', left: '58%', width: '26%', height: '28%', text: 'THREAD_ENGAGE' },
    ],
    verify: {
      outcomeTitle: 'Supply connection verified',
      outcomeBody:
        'Thread engagement within tolerance. Compression nut seated correctly. No visible leak at the fitting.',
      passBox: [
        { top: '42.33%', left: '54%', width: '34%', height: '17.33%', text: 'THREAD_ENGAGE' },
      ],
    },
  },
  {
    id: 'knockout',
    num: '2',
    label: 'Knockout Plug Removed',
    basePhoto: '/proof/proof-2-knockout.jpg',
    capture: [
      'View straight into disposal inlet',
      'Interior clearly visible',
      'Before attaching drain hose',
    ],
    why: 'A retained knockout plug is the most common cause of drainage failure on new installs. Verifying removal prevents an almost-certain callback.',
    primerBox: [
      { top: '26%', left: '30%', width: '40%', height: '54%', text: 'INLET_CLEAR' },
    ],
    cameraBox: [
      { top: '30%', left: '10%', width: '80%', height: '44%', text: 'INLET_CLEAR' },
    ],
    verify: {
      outcomeTitle: 'Knockout plug confirmed removed',
      outcomeBody:
        'Disposal inlet is clear. Drain path unobstructed. Safe to proceed with drain hose attachment.',
      passBox: [
        { top: '22%', left: '28%', width: '44%', height: '60%', text: 'INLET_CLEAR' },
      ],
    },
  },
  {
    id: 'drain',
    num: '3',
    label: 'Drain Hose at Disposal',
    basePhoto: '/proof/proof-3-drain.jpg',
    capture: [
      'Connection at disposal or tailpiece',
      'Hose clamp fully seated',
      'Hose routing clear in frame',
    ],
    why: 'A loose or detached hose clamp is a common installer-side failure that causes chronic leaks. A clear photo closes this claim class.',
    primerBox: [
      { top: '52%', left: '58%', width: '18%', height: '36%', text: 'CLAMP_SEATED' },
    ],
    cameraBox: [
      { top: '52%', left: '76%', width: '18%', height: '14%', text: 'CLAMP_SEATED' },
    ],
    verify: {
      outcomeTitle: 'Drain hose attachment verified',
      outcomeBody:
        'Hose clamp seated on ribbed section, fully tightened. Attachment is secure.',
      passBox: [
        { top: '50%', left: '70%', width: '22%', height: '22%', text: 'CLAMP_SEATED' },
      ],
    },
  },
  {
    id: 'highloop',
    num: '4',
    label: 'High Loop Position',
    basePhoto: '/proof/proof-4-highloop.jpg',
    capture: [
      'Full routing from appliance to disposal',
      'Hose elevation clearly visible',
      'Fastening to counter underside in frame',
    ],
    why: 'Missing high loop appeared in 8 of 12 claims analyzed — the single most common defect. One photo of the routed loop eliminates this defect class.',
    primerBox: [
      { top: '28%', left: '18%', width: '66%', height: '18%', text: 'HIGH_LOOP' },
    ],
    cameraBox: [
      { top: '18%', left: '18%', width: '66%', height: '18%', text: 'HIGH_LOOP' },
    ],
    verify: {
      outcomeTitle: 'High loop verified',
      outcomeBody:
        'Drain hose routes above the disposal inlet and is secured at the counter underside. Code-compliant.',
      passBox: [
        { top: '18%', left: '15%', width: '70%', height: '18%', text: 'HIGH_LOOP' },
      ],
    },
  },
  {
    id: 'overview',
    num: '5',
    label: 'Under-Sink Overview',
    basePhoto: '/proof/proof-5-overview.jpg',
    capture: [
      'Wide shot of full under-sink area',
      'Cabinet floor visible and dry',
      'Supply, drain, and disposal in frame',
    ],
    why: 'The wide shot after water-on establishes that the cabinet floor is dry at departure. It protects against prior-damage claims.',
    primerBox: [
      { top: '55%', left: '10%', width: '78%', height: '32%', text: 'CABINET_DRY' },
    ],
    cameraBox: [
      { top: '55%', left: '10%', width: '78%', height: '32%', text: 'CABINET_DRY' },
    ],
    verify: {
      outcomeTitle: 'Under-sink overview verified',
      outcomeBody:
        'Cabinet floor dry. No moisture, staining, or pooling detected. Safe to close the job.',
      passBox: [
        { top: '55%', left: '10%', width: '78%', height: '32%', text: 'CABINET_DRY' },
      ],
    },
  },
]

export const HIGHLOOP_FAIL: {
  photo: string
  cameraBox: DetectionBoxSpec
  failBox: DetectionBoxSpec
  outcomeTitle: string
  outcomeBody: string
} = {
  photo: '/proof/proof-4-highloop-fail.jpg',
  cameraBox: {
    top: '50%',
    left: '36%',
    width: '44%',
    height: '20%',
    text: 'HIGH_LOOP · TOO LOW',
  },
  failBox: {
    top: '50%',
    left: '36%',
    width: '44%',
    height: '40%',
    text: 'HIGH_LOOP · TOO LOW',
  },
  outcomeTitle: 'High loop is too low',
  outcomeBody:
    'The drain hose peaks below the disposal inlet. Code requires the loop to be fastened to the underside of the counter, well above the disposal connection, to prevent backflow.',
}
